import React, { ForwardRefRenderFunction, useState, useEffect, forwardRef, useMemo } from 'react';
import type { Questionnaire } from '@/typings/Questionnaire';
import * as QuestionnaireApiModels from '@/typings/questionnaires-api-models';
import { QuestionnaireProvider } from '@/components/Questionnaire/QuestionnaireProvider';
import ProgressIndicator from '@components/ProgressIndicator';
import classNames from 'classnames';
import { useFormEditorContext } from '@components/Questionnaire/questionnaireEditor/FormEditor/FormEditorContext';
import TabText from '@/components/DesignLibrary/Atoms/TabText';
import removeNullish from '@/helpers/removeNullish';
import s from './FormPreview.module.scss';
import FormItem from './FormItem';

type FormPreviewProps = {
  formData: Questionnaire.Questionnaire;
  assignees: QuestionnaireApiModels.QuestionnaireAssigneeTemplate[];
  currentSection: Questionnaire.Section;
  currentField: string | undefined;
  onSelectField: (args: { field: Questionnaire.Field, isInList: boolean }) => void;
  onReorderFields: Parameters<typeof FormItem>[0]['onReorder'];
};

const FormPreview: ForwardRefRenderFunction<HTMLDivElement, FormPreviewProps> = ({
  currentField,
  assignees,
  currentSection,
  onSelectField,
  formData,
  onReorderFields,
}, ref) => {
  const [selectedAssignee, setSelectedAssignee] = useState<string>('All');
  const [currentSubsection, setCurrentSubsection] = useState<string | null>(null);

  const { setSelectedFieldEditorTab } = useFormEditorContext();

  const currentSectionData = useMemo(
    () => formData.sections.find(section => section.id === currentSection.id),
    [formData.sections, currentSection],
  );

  useEffect(() => {
    setCurrentSubsection(currentSectionData?.subsections?.[0] || null);
  }, [currentSectionData?.subsections]);

  const handleAssigneeFilter = (assignee: string): void => {
    setSelectedAssignee(assignee);
  };

  const filteredFields = useMemo(() => currentSection.fields.filter(field =>
    (selectedAssignee === 'All' || field.assignedTo.includes(selectedAssignee)) && !field.listFieldTemplateId && (currentSubsection ? field.subsection === currentSubsection : true),
  ), [currentSection, selectedAssignee, currentSubsection]);

  const fieldsByKey: Record<string, Questionnaire.Field> = useMemo(() => formData
    ? formData.sections.reduce((acc, val) => ({
      ...acc,
      ...val.fields.reduce((acc2, val2) => ({
        ...acc2,
        [val2.key]: val2,
      }), []),
    }), [])
    : {}, [formData]);

  return (
    <div className={s.formPreview}>
      <div className={s.formContent}>
        {assignees.length > 0 && <div className={s.assigneeFilters}>
          <button
            className={selectedAssignee === 'All' ? s.activeFilter : ''}
            onClick={() => handleAssigneeFilter('All')}
          >
            All
          </button>
          {assignees.map(assignee => (
            <button
              key={assignee.id}
              className={selectedAssignee === assignee.id ? s.activeFilter : ''}
              onClick={() => handleAssigneeFilter(assignee.id)}
            >
              {assignee.label}
            </button>
          ))}
        </div>}
        <div className={s.sectionTitle}>
          <h2>{currentSection.label}</h2>
          <p>{currentSection.description}</p>
        </div>
        {(currentSectionData?.subsections?.length || 0) > 0 && (
          <div className={s.tabs}>
            {[
              ...(currentSectionData?.subsections || []),
              ...(currentSectionData?.fields ?? []).filter((f) => !(currentSectionData?.subsections || []).includes(f.subsection || '')).map((f) => f.subsection),
            ].filter(removeNullish).map((subsection) => (
              <TabText
                key={subsection}
                label={subsection}
                selected={currentSubsection === subsection}
                onClick={() => setCurrentSubsection(subsection === currentSubsection ? null : subsection)}
              />
            ))}
          </div>
        )}
        {currentSection.isLoading && (
          <ProgressIndicator isCentered />
        )}
        <QuestionnaireProvider enabled aiAutofillActions={['ai-agent', 'get-filing-county', 'get-filing-district']} fieldsByKey={fieldsByKey} formData={formData} questionnaireResponses={[]} onSubmit={async () => true} userWorkflowId={null}>
          <div className={s.form}>
            {filteredFields.map(field => (
              <div
                key={field.id}
                id={field.id}
                className={classNames(s.field, field.id === currentField && s.selectedField)}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  onSelectField({ field, isInList: false });
                  setSelectedFieldEditorTab('settings');
                }}
              >
                <FormItem
                  item={field}
                  assignees={
                    assignees
                      .filter(({ id }) => field.assignedTo.includes(id))
                      .map(({ label }) => label)
                  }
                  currentSelectedField={currentField}
                  fields={currentSection.fields}
                  subFields={field.listItemTemplates}
                  isSelected={field.id === currentField}
                  conditionExpression={field.conditionExpression || undefined}
                  onClick={onSelectField}
                  onReorder={onReorderFields}
                  reorderDownDisabled={Boolean(currentSubsection) || filteredFields[filteredFields.length - 1].id === field.id}
                  reorderUpDisabled={Boolean(currentSubsection) || filteredFields[0].id === field.id}
                />
              </div>
            ))}
          </div>
          <div ref={ref} />
        </QuestionnaireProvider>
      </div>
    </div>
  );
};

export default forwardRef(FormPreview);
